import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import React from 'react'
import {
    ApplyBand,
    buttonSx,
    classToFComponent,
    CollapsedPaneFilterIcon,
    CollapsedPaneHeader,
    CollapsedPaneLabel,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    FilterPaneContainer,
    FilterPaneHeader,
    IconActionButton,
    inputSx,
    PaneTitle
} from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'

import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'

import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'

import { AccessFilterPaneScope } from '../aa_scopes'

const LOG = Logger.get('AA-FILTER-VIEW')

const FilterBody = styled('div')({
    padding: '18px 16px 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto'
})

const useStyles = makeStyles()({
    expanded: {
        width: '384px'
    },
    shrinked: {
        minWidth: 36
    }
})

type UserOption = { id: number; name: string }

type FilterPaneProps = {
    className?: string
    scope: AccessFilterPaneScope
}

class FilterPaneViewClass implements FCClassContext<FilterPaneProps> {
    // :: Fields

    scope!: AccessFilterPaneScope
    classes!: ReturnType<typeof useStyles>['classes']

    // :: Emissors

    readonly emitToggleExpanded = () => this.scope.onToggleExpanded().catch(LOG.caught)
    readonly emitClear = () => this.scope.onClear().catch(LOG.caught)
    readonly emitApply = () => this.scope.onApply().catch(LOG.caught)

    readonly emitStartChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onStartChanged(e.target.value).catch(LOG.caught)

    readonly emitEndChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onEndChanged(e.target.value).catch(LOG.caught)

    readonly emitGroupChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onGroupChanged(e.target.value ? Number(e.target.value) : undefined).catch(LOG.caught)

    readonly emitPageChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onPageChanged(e.target.value || undefined).catch(LOG.caught)

    readonly emitUserChanged = (_event: unknown, option: UserOption | null) =>
        this.scope.onUserChanged(option?.id).catch(LOG.caught)

    // :: Methods

    render({ className, scope }: FilterPaneProps) {
        this.scope = scope
        this.classes = useStyles().classes

        return (
            <FilterPaneContainer
                className={clsx(scope.expanded ? this.classes.expanded : this.classes.shrinked, className)}
            >
                {scope.expanded ? this.renderOpenFilter() : this.renderCloseFilter()}
            </FilterPaneContainer>
        )
    }

    renderOpenFilter() {
        const { scope } = this

        return (
            <>
                <FilterPaneHeader>
                    <FilterListIcon />
                    <PaneTitle>Filtros</PaneTitle>
                    <Tooltip title="Ocultar filtros">
                        <IconActionButton size="small" aria-label="Ocultar filtros" onClick={this.emitToggleExpanded}>
                            <KeyboardDoubleArrowRightIcon />
                        </IconActionButton>
                    </Tooltip>
                </FilterPaneHeader>
                <FilterBody>
                    <Field>
                        <FieldLabel htmlFor="aaStart">Data inicial</FieldLabel>
                        <TextField
                            id="aaStart"
                            type="date"
                            size="small"
                            value={scope.start}
                            onChange={this.emitStartChanged}
                            sx={inputSx}
                            fullWidth
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="aaEnd">Data final</FieldLabel>
                        <TextField
                            id="aaEnd"
                            type="date"
                            size="small"
                            value={scope.end}
                            onChange={this.emitEndChanged}
                            sx={inputSx}
                            fullWidth
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                        />
                        <FieldHelp>O período vale para os indicadores, os gráficos e as grades.</FieldHelp>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="aaGroup">Departamento</FieldLabel>
                        <TextField
                            id="aaGroup"
                            select
                            size="small"
                            value={scope.groupId ?? ''}
                            onChange={this.emitGroupChanged}
                            sx={inputSx}
                            fullWidth
                            slotProps={{
                                select: { displayEmpty: true }
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {scope.groupOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="aaUser">Usuário</FieldLabel>
                        <Autocomplete
                            id="aaUser"
                            options={scope.userOptions}
                            value={scope.userOptions.find((option) => option.id === scope.userId) ?? null}
                            onChange={this.emitUserChanged}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            size="small"
                            noOptionsText="Nenhum usuário encontrado"
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Todos os usuários" sx={inputSx} />
                            )}
                            fullWidth
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="aaPage">Relatório</FieldLabel>
                        <TextField
                            id="aaPage"
                            select
                            size="small"
                            value={scope.placeCode ?? ''}
                            onChange={this.emitPageChanged}
                            sx={inputSx}
                            fullWidth
                            slotProps={{
                                select: { displayEmpty: true }
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {scope.pageOptions.map((option) => (
                                <MenuItem key={option.placeCode} value={option.placeCode}>
                                    {option.pageLabel}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FieldHelp>
                            Restringe a apuração a um relatório — os usuários sem acesso a ele passam a contar como
                            inativos.
                        </FieldHelp>
                    </Field>
                </FilterBody>
                <ApplyBand>
                    <Button
                        sx={buttonSx()}
                        onClick={this.emitClear}
                        disabled={!scope.isFiltering}
                        startIcon={<CleaningServicesIcon />}
                    >
                        Limpar filtros
                    </Button>
                    <Button sx={buttonSx('primary')} onClick={this.emitApply}>
                        Aplicar
                    </Button>
                </ApplyBand>
            </>
        )
    }

    renderCloseFilter() {
        return (
            <CollapsedPaneHeader>
                <Tooltip title="Mostrar filtros">
                    <IconActionButton size="small" aria-label="Mostrar filtros" onClick={this.emitToggleExpanded}>
                        <KeyboardDoubleArrowLeftIcon />
                    </IconActionButton>
                </Tooltip>
                {this.scope.isFiltering && (
                    <Tooltip title="Limpar filtros">
                        <IconActionButton size="small" aria-label="Limpar filtros" onClick={this.emitClear}>
                            <CleaningServicesIcon />
                        </IconActionButton>
                    </Tooltip>
                )}
                <CollapsedPaneFilterIcon />
                <CollapsedPaneLabel>Filtros</CollapsedPaneLabel>
            </CollapsedPaneHeader>
        )
    }
}

export const AccessFilterPaneView = classToFComponent(FilterPaneViewClass, React)
export default AccessFilterPaneView
